import React, { useState, useEffect } from 'react';
import { AllowedGuard } from 'components/Tvendo/Common/UserAction';
import { ConstantPermissions } from 'contexts/useConstant';
import { makeStyles } from '@material-ui/core/styles';
import SweetAlert from 'react-bootstrap-sweetalert';
import alertStyles from 'assets/jss/material-dashboard-pro-react/views/sweetAlertStyle.js';

//iconos
import { Create, Add, Weekend, PhotoCamera, Block } from '@material-ui/icons';

//tabla y card components
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Button from 'components/CustomButtons/Button.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardIcon from 'components/Card/CardIcon.js';
import CardHeader from 'components/Card/CardHeader.js';
import ReactTable from 'components/ReactTable/ReactTableV2.js';
import { cardTitle } from 'assets/jss/material-dashboard-pro-react.js';
import LoadingSpinner from 'components/Tvendo/Common/LoadingSpinner';

//servicios
import { wsGetAll, wsUpdate } from 'services/WService.js';

//modal
import Modal from './Modal';
import PhotoModal from 'components/Tvendo/Common/PhotoModal';
import { useModal } from 'hooks/useModal';
import { nombreEstadoMueble, ESTADO_BAJA } from './estados';
import { Toasty, useToasty } from 'hooks/Toasty';
import ButtonDelete from 'hooks/ButtonDelete';
import { WithApp } from 'contexts/appContext';

const styles = {
  cardIconTitle: {
    ...cardTitle,
    marginTop: '15px',
    marginBottom: '0px'
  }
};
const useStyles = makeStyles(styles);
const useAlertStyles = makeStyles(alertStyles);

//columnas para llenar la tabla
const columns = [
  { Header: '#', accessor: 'num', excelEnabled: false, width: 190 },
  { Header: 'Código', accessor: 'code', width: 130 },
  { Header: 'N° de serie', accessor: 'serial', width: 150 },
  { Header: 'Descripción', accessor: 'description' },
  { Header: 'Foto', accessor: 'photo', excelEnabled: false, width: 80 },
  { Header: 'Categoría', accessor: 'category', width: 160 },
  { Header: 'Estado', accessor: 'state', width: 130 },
  { Header: '', accessor: 'actions', excelEnabled: false, width: 0 },
];

/** Mantenimiento de muebles. El código puede quedar en blanco. */
export default WithApp(function Furniture(props) {
  const classes = useStyles();
  const alertClasses = useAlertStyles();

  const [alert, setAlert] = useState(null);
  const [model, setOpen] = useModal();
  const [modelFoto, setOpenFoto] = useModal();
  const [notify, showNotify] = useToasty();
  const [furnitures, setFurnitures] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);

  async function loadFurnitures() {
    setDataLoading(true);
    let response = await wsGetAll('base/furniture');
    setDataLoading(false);
    if (response.success) setFurnitures(response.data);
  }

  useEffect(() => {
    loadFurnitures();
  }, []);

  const hideAlert = () => setAlert(null);

  /**
   * Baja del mueble. Es la única transición que se hace a mano: entre
   * Asignado y Almacén lo mueve el control.
   *
   * Si el mueble sigue puesto en un cliente el backend lo rechaza y dice
   * cuál, así que ese mensaje se muestra tal cual en vez de inventar uno.
   */
  async function darDeBaja(item) {
    let response = await wsUpdate('base/furniture', item.id, { state: ESTADO_BAJA });
    if (response.success) {
      showNotify(response.message);
      loadFurnitures();
    } else {
      showNotify(response.message, 'error');
    }
  }

  function confirmarBaja(item) {
    setAlert(
      <SweetAlert
        warning
        style={{ display: 'block', marginTop: '-100px' }}
        title="¿Dar de baja este mueble?"
        onConfirm={() => {
          darDeBaja(item);
          hideAlert();
        }}
        onCancel={() => hideAlert()}
        confirmBtnCssClass={alertClasses.button + ' ' + alertClasses.success}
        cancelBtnCssClass={alertClasses.button + ' ' + alertClasses.danger}
        confirmBtnText="Aceptar"
        cancelBtnText="Cancelar"
        showCancel
      >
        {item.code} - {item.description}
      </SweetAlert>
    );
  }

  return (
    <GridContainer>
      <Toasty action={notify} />
      {alert}
      {model.open ? (
        <Modal model={model} setOpen={setOpen} showNotify={showNotify} reload={loadFurnitures} />
      ) : undefined}
      {modelFoto.open ? (
        <PhotoModal
          model={modelFoto}
          setOpen={setOpenFoto}
          showNotify={showNotify}
          reload={loadFurnitures}
          entity="base/furniture"
          texto="del mueble"
          updatePermission={ConstantPermissions.BASE_FURNITURES_UPDATE}
        />
      ) : undefined}

      <GridItem xs={12}>
        <Card>
          <CardHeader icon>
            <AllowedGuard
              permission={ConstantPermissions.BASE_FURNITURES_STORE}
              component={
                <CardIcon color="primary">
                  <Weekend style={{ color: 'white' }} />
                </CardIcon>
              }
            >
              <CardIcon
                onClick={() => setOpen({})}
                pointer="true"
                color={props.themeContext.buttonColorSuccess}
              >
                <Add />
              </CardIcon>
            </AllowedGuard>
            <h4 className={classes.cardIconTitle}>
              Listado de muebles {dataLoading && <LoadingSpinner />}
            </h4>
          </CardHeader>
          <CardBody>
            <ReactTable
              excelName="Muebles"
              defaultWidth={900}
              columns={columns}
              data={furnitures.map((item, i) => {
                return {
                  num: (
                    <div className="actions-right">
                      {i + 1}
                      <AllowedGuard permission={ConstantPermissions.BASE_FURNITURES_SHOW}>
                        <Button
                          justIcon
                          round
                          simple
                          color={props.themeContext.buttonColorWarning}
                          onClick={() => setOpenFoto(item)}
                        >
                          <PhotoCamera />
                        </Button>
                      </AllowedGuard>
                      <AllowedGuard permission={ConstantPermissions.BASE_FURNITURES_UPDATE}>
                        <Button
                          justIcon
                          round
                          simple
                          color={props.themeContext.buttonColor}
                          onClick={() => setOpen(item)}
                        >
                          <Create />
                        </Button>
                      </AllowedGuard>
                      {/* La baja solo tiene sentido si no está ya dado de baja */}
                      {item.state !== ESTADO_BAJA ? (
                        <AllowedGuard permission={ConstantPermissions.BASE_FURNITURES_UPDATE}>
                          <Button
                            justIcon
                            round
                            simple
                            color={props.themeContext.buttonColorDanger}
                            title="Dar de baja"
                            onClick={() => confirmarBaja(item)}
                          >
                            <Block />
                          </Button>
                        </AllowedGuard>
                      ) : null}
                      <AllowedGuard permission={ConstantPermissions.BASE_FURNITURES_DESTROY}>
                        <ButtonDelete entity="base/furniture" id={item.id} reload={loadFurnitures} />
                      </AllowedGuard>
                    </div>
                  ),
                  //código y serie quedan vacíos hasta que el usuario los complete
                  code: item.code || '',
                  serial: item.serial_number || '',
                  description: item.description,
                  //la miniatura abre el modal en vez de la imagen suelta
                  photo: item.file_url ? (
                    <img
                      src={item.file_url}
                      alt=""
                      onClick={() => setOpenFoto(item)}
                      style={{
                        width: 38,
                        height: 38,
                        objectFit: 'cover',
                        borderRadius: 4,
                        cursor: 'pointer',
                      }}
                    />
                  ) : (
                    ''
                  ),
                  category: item.category?.description || '',
                  state: nombreEstadoMueble(item.state),
                  actions: '',
                };
              })}
            />
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
});
